import { type EntityProfile, type FieldDef, resolveProfile, type SchemaSnapshot } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, FolderKanban, MessagesSquare, SearchX } from 'lucide-react';
import { InCases } from '../components/cases/in-cases';
import { SimilarEntities } from '../components/clusters/similar-entities';
import { DataText } from '../components/data/data-text';
import { ConnectionsGraph } from '../components/entities/connections-graph';
import { REVIEW_STATUS_LABELS } from '../components/entities/entity-link';
import { MergeHistory } from '../components/entities/merge';
import { FieldRow } from '../components/entities/profile-field';
import { ProfileHeader } from '../components/entities/profile-header';
import { RelationsSection } from '../components/entities/relations';
import { EraseSection } from '../components/erasure/erase-dialog';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { MentionedIn } from '../components/library/mentioned-in';
import { PossibleSame } from '../components/review/same-person';
import { RefSearchContext } from '../components/schema/field-input';
import { ApiError } from '../lib/api';
import { useEntitySearch, useProfile } from '../lib/entities';
import { useSchema } from '../lib/schema';

// Entity profile (PLAN §12.7 screen 6): laid out by the type's profile template; every value is a claim with its
// kind, grade and review status; editors change values inline (a new verified claim supersedes the old one).

export const Route = createFileRoute('/entity/$id')({ component: ProfilePage });

const TOUR = {
  id: 'entity-profile',
  steps: [
    {
      target: '[data-tour="profile-header"]',
      title: 'Header',
      body: 'ID code, every name in every script, type, review status and badges. Hover a badge to see what it means.',
      learnMore: 'directory',
    },
    {
      target: '[data-tour="profile-blocks"]',
      title: 'Blocks of the profile sheet',
      body: 'Each value is a claim. Its left border shows its review status; chips show its kind, grade and sources. Use ⓘ for what a field means.',
      learnMore: 'claims-and-profiles',
    },
    {
      target: '[data-tour="profile-relations"]',
      title: 'Relations',
      body: 'Links to other entities, shown on both profiles, each from its own side.',
    },
  ],
};

function ProfilePage() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const { data: snap } = useSchema();
  const profile = useProfile(id);
  const search = useEntitySearch(snap, id);
  if (profile.isError)
    return (
      <EmptyState
        icon={SearchX}
        title={
          profile.error instanceof ApiError && profile.error.status === 404
            ? 'This entity does not exist'
            : 'The profile could not be loaded'
        }
        learnMore="directory"
      >
        <Link to="/directory" className="underline">
          Back to the directory
        </Link>
      </EmptyState>
    );
  if (!snap || !profile.data)
    return (
      <div className="space-y-3" aria-busy="true">
        <span className="sr-only">Loading…</span>
        <div className="h-28 animate-pulse rounded-lg bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  const p = profile.data;
  return (
    <RefSearchContext.Provider value={search}>
      <div className="space-y-4">
        <Link
          to="/directory"
          search={{ kind: p.entity.kind }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" /> Directory
        </Link>
        <div data-tour="profile-header">
          <ProfileHeader profile={p} snap={snap} />
        </div>
        {p.entity.kind === 'case' && (
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to="/cases/$id"
              params={{ id: p.entity.id }}
              className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
            >
              <FolderKanban className="size-4" aria-hidden="true" /> Open the Case workspace
            </Link>
            <Link
              to="/chat"
              search={{ case: p.entity.id }}
              className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
            >
              <MessagesSquare className="size-4" aria-hidden="true" /> Ask the chat about this Case
            </Link>
          </p>
        )}
        <MergeHistory profile={p} snap={snap} />
        <PossibleSame entityId={p.entity.id} snap={snap} />
        <Tip id="entity-profile" learnMore="claims-and-profiles">
          Editing a value keeps the old one in its history. Values you type are verified, with you as their source.
        </Tip>
        {p.disputed.length > 0 && <DisputedPanel profile={p} />}
        <div data-tour="profile-blocks" className="space-y-4">
          <Blocks profile={p} snap={snap} />
        </div>
        <ConnectionsGraph profile={p} snap={snap} />
        {p.entity.kind !== 'case' && <SimilarEntities entityId={p.entity.id} cluster={p.entity.cluster} schema={snap} />}
        <div data-tour="profile-relations">
          <RelationsSection profile={p} snap={snap} />
        </div>
        <MentionedIn entityId={p.entity.id} />
        {p.entity.kind !== 'case' && <InCases schema={snap} target={{ entity: p.entity.id }} />}
        {!p.entity.mergedIntoId && <EraseSection kind="entity" id={p.entity.id} name={p.entity.displayName} />}
      </div>
    </RefSearchContext.Provider>
  );
}

/** Conflicting values at a glance; each field below shows them side by side (PLAN §2 "disputed"). */
function DisputedPanel({ profile }: { profile: EntityProfile }) {
  const fields = profile.disputed.map(k => profile.fields[k]).filter(f => !!f);
  const relations = profile.relations.filter(r => profile.disputed.includes(r.claimId));
  return (
    <section aria-labelledby="disputed-heading" className="space-y-2 rounded-lg border-[3px] border-double border-warning bg-surface p-4">
      <h2 id="disputed-heading" className="flex items-center gap-2 font-semibold">
        <span aria-hidden="true">⚠</span> Disputed
      </h2>
      <p className="text-sm text-muted-foreground">
        Sources disagree on these. Reviewers verify the right value or reject the others; until then all are shown.
      </p>
      <ul className="space-y-1 text-sm">
        {fields.map(f => (
          <li key={f.key}>
            <a href={`#field-${f.key}`} className="font-medium underline">
              {f.label}
            </a>
            :{' '}
            {f.values.map((v, i) => (
              <span key={v.claimId}>
                {i > 0 && ' · '}
                <DataText>{v.display}</DataText>{' '}
                <span className="text-muted-foreground">({REVIEW_STATUS_LABELS[v.status].toLowerCase()})</span>
              </span>
            ))}
          </li>
        ))}
        {relations.map(r => (
          <li key={r.claimId}>
            {r.label} <DataText>{r.otherEntity?.displayName ?? ''}</DataText>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={`block-${id}`} className="rounded-lg border bg-surface p-4">
      <h2 id={`block-${id}`} className="mb-1 font-semibold">
        {title}
      </h2>
      <div className="divide-y">{children}</div>
    </section>
  );
}

function Blocks({ profile, snap }: { profile: EntityProfile; snap: SchemaSnapshot }) {
  const layout = resolveProfile(snap, profile.entity.typeId);
  if (!layout) return null;
  const shown = new Set<string>();
  const row = (f: FieldDef) => {
    shown.add(f.key);
    return (
      <div key={f.key} id={`field-${f.key}`}>
        <FieldRow entityId={profile.entity.id} def={f} field={profile.fields[f.key]} snap={snap} />
      </div>
    );
  };
  const blocks = layout.blocks.filter(b => b.fields.length);
  const other = layout.otherGroups;
  const sections = [
    ...blocks.map(b => (
      <Section key={b.key} id={b.key} title={b.number ? `${b.number} · ${b.label}` : b.label}>
        {b.fields.map(row)}
      </Section>
    )),
    ...other.map(g => (
      <Section key={g.key} id={g.key} title={g.label}>
        {g.fields.map(row)}
      </Section>
    )),
  ];
  // Values of fields no longer in the layout (archived, or from another type before a type change) stay visible.
  const orphans = Object.values(profile.fields).filter(f => !shown.has(f.key) && f.key !== 'tag');
  return (
    <>
      {sections}
      {orphans.length > 0 && (
        <Section id="other-values" title="Other values">
          {orphans.map(f => (
            <div key={f.key} id={`field-${f.key}`}>
              <FieldRow entityId={profile.entity.id} field={f} snap={snap} />
            </div>
          ))}
        </Section>
      )}
    </>
  );
}
